import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { GISMapEditor } from "../components/ui/gis-map-editor";
import {
  Building2,
  ArrowLeft,
  MapPin,
  Ruler,
  User,
  Save,
  Loader2,
  Map,
  FileText,
  Calendar,
} from "lucide-react";

const PropertyDetailPage = () => {
  const { propertyId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  
  // Form state
  const [formData, setFormData] = useState({
    lot_number: "",
    address: "",
    block: "",
    phase: "",
    acreage: "",
    parcel_id: "",
    notes: "",
    owner_id: "",
    legal_description: "",
    status: "vacant",
  });

  // GIS state
  const [gisData, setGisData] = useState({
    gis_centroid: null,
    gis_lot_boundary: null,
    geometries: [],
  });
  const [gisChanged, setGisChanged] = useState(false);

  useEffect(() => {
    fetchProperty();
    fetchOwners();
  }, [propertyId, token]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const prop = response.data;
      setProperty(prop);
      setFormData({
        lot_number: prop.lot_number || "",
        address: prop.address || "",
        block: prop.block || "",
        phase: prop.phase || "",
        acreage: prop.acreage?.toString() || "",
        parcel_id: prop.parcel_id || "",
        notes: prop.notes || "",
        owner_id: prop.owner_id || "",
        legal_description: prop.legal_description || "",
        status: prop.status || "vacant",
      });
      setGisData({
        gis_centroid: prop.gis_centroid || null,
        gis_lot_boundary: prop.gis_lot_boundary || null,
        geometries: prop.geometries || [],
      });
    } catch (error) {
      toast.error("Failed to load property");
      navigate("/board/properties");
    } finally {
      setLoading(false);
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await axios.get(`${API}/owners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOwners(response.data || []);
    } catch (error) {
      console.error("Failed to fetch owners:", error);
    }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    try {
      const updateData = {
        ...formData,
        acreage: formData.acreage ? parseFloat(formData.acreage) : null,
        owner_id: formData.owner_id || null,
      };
      
      await axios.put(`${API}/properties/${propertyId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Property details saved");
      fetchProperty();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save property");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGIS = async (gisPayload) => {
    setSaving(true);
    try {
      await axios.put(`${API}/properties/${propertyId}`, gisPayload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGisData(gisPayload);
      setGisChanged(false);
      toast.success("GIS data saved");
      fetchProperty();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save GIS data");
    } finally {
      setSaving(false);
    }
  };

  const handleGISChange = (newGisData) => {
    setGisData(newGisData);
    setGisChanged(true);
  };

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Property not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="property-detail-page">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/board/properties")}
              className="text-muted-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-primary" />
              <div>
                <h1 className="font-serif text-lg font-semibold">Lot {property.lot_number}</h1>
                <p className="text-sm text-muted-foreground">{property.address}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {property.gis_lot_boundary && (
              <Badge variant="outline" className="text-green-600 border-green-200">
                <Map className="w-3 h-3 mr-1" />
                GIS Mapped
              </Badge>
            )}
            {property.owner_id && (
              <Badge variant="outline">
                <User className="w-3 h-3 mr-1" />
                Assigned
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="details" className="gap-2">
              <FileText className="w-4 h-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="gis" className="gap-2">
              <Map className="w-4 h-4" />
              GIS Mapping
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Basic Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Lot Number *</Label>
                      <Input
                        value={formData.lot_number}
                        onChange={(e) => updateFormData("lot_number", e.target.value)}
                        data-testid="lot-number-input"
                      />
                    </div>
                    <div>
                      <Label>Parcel ID</Label>
                      <Input
                        value={formData.parcel_id}
                        onChange={(e) => updateFormData("parcel_id", e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Address *</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => updateFormData("address", e.target.value)}
                      data-testid="address-input"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Block</Label>
                      <Input
                        value={formData.block}
                        onChange={(e) => updateFormData("block", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Phase</Label>
                      <Input
                        value={formData.phase}
                        onChange={(e) => updateFormData("phase", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Acreage</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.acreage}
                        onChange={(e) => updateFormData("acreage", e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(v) => updateFormData("status", v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vacant">Vacant</SelectItem>
                        <SelectItem value="built">Built</SelectItem>
                        <SelectItem value="under_construction">Under Construction</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Owner & Notes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Owner & Notes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Owner</Label>
                    <Select
                      value={formData.owner_id || "none"}
                      onValueChange={(v) => updateFormData("owner_id", v === "none" ? "" : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select owner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No owner assigned</SelectItem>
                        {owners.map((owner) => (
                          <SelectItem key={owner.owner_id} value={owner.owner_id}>
                            {owner.name || `${owner.first_name} ${owner.last_name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Legal Description</Label>
                    <Textarea
                      value={formData.legal_description}
                      onChange={(e) => updateFormData("legal_description", e.target.value)}
                      placeholder="Legal property description..."
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label>Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => updateFormData("notes", e.target.value)}
                      placeholder="Additional notes..."
                      rows={4}
                    />
                  </div>

                  <Button
                    onClick={handleSaveDetails}
                    disabled={saving}
                    className="w-full"
                    data-testid="save-details-btn"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Details
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* GIS Tab */}
          <TabsContent value="gis">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Map className="w-4 h-4" />
                  GIS Mapping
                  {gisChanged && (
                    <Badge variant="secondary" className="ml-2">Unsaved changes</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <GISMapEditor
                  propertyId={propertyId}
                  initialGeometries={gisData.geometries}
                  initialLotBoundary={gisData.gis_lot_boundary}
                  initialCentroid={gisData.gis_centroid}
                  onSave={handleSaveGIS}
                  onGeometryChange={handleGISChange}
                  height="600px"
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PropertyDetailPage;
